import React, { SetStateAction } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { JurisdictionCodes } from 'src/components/Common/Jurisdiction/types';
import useQueryParam from 'src/lib/Hooks/useQueryParam';
import setJurisdictionCookie from 'src/lib/Utils/setJurisdictionCookie';
import { JurisdictionIdType } from '..';
import Images from '../lib/images';
import { Select } from './Select';
import Header from './header';

type JurisdictionType = {
  id: string;
  img: string;
  name: string;
};

type JurisdictionComponentType = {
  areNoOffers: boolean;
  selectedState: string;
  jurisdiction: string | undefined;
  setJurisdictionId: React.Dispatch<SetStateAction<undefined | string>>;
  setSelectedState: (arg0: string) => void;
};

type JurisdictionDropdownType = {
  jurisdiction: string | undefined;
  setJurisdictionId: React.Dispatch<SetStateAction<undefined | string>>;
  setSelectedState: (arg0: string) => void;
};

export const isValidJurisdictionId = (id: any): id is JurisdictionIdType => {
  return JurisdictionCodes.includes(id);
};

const JurisdictionDropdown = ({
  jurisdiction,
  setJurisdictionId,
  setSelectedState,
}: JurisdictionDropdownType) => {
  const onChange = ({ value }: { value: string }) => {
    if (value !== 'NC' && value !== 'SC') {
      setSelectedState('');
      setJurisdictionId(value);
    } else {
      setSelectedState(value);
      setJurisdictionId(undefined);
    }
  };
  const methods = useForm();

  const statesList = [
    {
      value: 'NC01',
      text: 'Duke Energy Carolinas-NC',
    },
    {
      value: 'NC02',
      text: 'Duke Energy Progress-NC',
    },
    {
      value: 'NC',
      text: 'North Carolina',
    },
    {
      value: 'SC01',
      text: 'Duke Energy Carolinas-SC',
    },
    {
      value: 'SC02',
      text: 'Duke Energy Progress-SC',
    },
    {
      value: 'SC',
      text: 'South Carolina',
    },
    {
      value: 'IN01',
      text: 'Indiana',
    },
    {
      value: 'KY01',
      text: 'Kentucky',
    },
  ];

  const filterItems = (jurisdiction: string | undefined) => {
    if (jurisdiction) {
      if (jurisdiction.includes('NC')) {
        statesList.splice(2, 3);
      } else if (jurisdiction.includes('SC')) {
        statesList.splice(5, 1);
        statesList.splice(0, 2);
      } else {
        statesList.splice(3, 2);
        statesList.splice(0, 2);
      }
    }
    return statesList;
  };

  const getCurrentItem = (jurisdiction: string | undefined) => {
    return statesList.find(state => state.value === jurisdiction);
  };

  const currentItem = getCurrentItem(jurisdiction) || { value: '', text: '' };
  const selectOptions = {
    defaultValue: currentItem.value,
    id: 'droplist-resultsperpage',
    items: filterItems(jurisdiction),
    label: currentItem.text,
    name: 'droplist-resultsperpage',
  };

  const unsupportedJurisdiction =
    typeof statesList.find(item => item.value === jurisdiction) === 'undefined';
  if (unsupportedJurisdiction) {
    return null;
  }

  return (
    <div className="py-32 full-width border-b border-gray">
      <FormProvider {...methods}>
        <form className="px-24 mx-auto w-fit sm:flex sm:flex-row sm:items-center">
          <span className="inline-block mb-16 sm:mb-0 sm:mr-12 text-gray-darker text-lg">
            Showing featured offers for:{' '}
          </span>
          <Select
            {...selectOptions}
            jurisdiction={jurisdiction}
            error={{ hasError: false }}
            onChange={onChange}
            className="text-blue text-lg bg-transparent bg-no-repeat appearance-none pr-16"
            style={{
              backgroundImage: `url(${Images.caret})`,
              backgroundSize: '12px',
              backgroundPosition: 'right center',
            }}
          />
        </form>
      </FormProvider>
    </div>
  );
};

const Jurisdiction = ({
  jurisdiction,
  setJurisdictionId,
  setSelectedState,
  selectedState,
}: JurisdictionComponentType) => {
  const history = useHistory();
  const [qpJurisdiction] = useQueryParam('jurisdiction');
  const setCookie = (id: JurisdictionIdType) => {
    if (id) {
      setJurisdictionCookie(id);
    }
  };

  const setJurisdiction = (id: JurisdictionIdType) => {
    setJurisdictionId(id);
    setCookie(id);
    // clearing jurisdiction query param when jx cookie changes in case user clicks "Previous" on step3 of Find a Contractor, don't want to show old jx data
    qpJurisdiction && history.replace({});
  };

  const backToState = () => {
    setSelectedState('');
  };

  const selectJurisdiction = (id: string) => {
    if (isValidJurisdictionId(id)) {
      setJurisdiction(id);
    }
  };

  const states = [
    // {
    //   id: 'FL',
    //   img: Images.jurisdiction.florida,
    //   name: 'Florida',
    // },
    {
      id: 'IN',
      img: Images.jurisdiction.indiana,
      name: 'Indiana',
    },
    {
      id: 'KY',
      img: Images.jurisdiction.kentucky,
      name: 'Kentucky',
    },
    // {
    //   id: 'OH',
    //   img: Images.jurisdiction.ohio,
    //   name: 'Ohio',
    // },
    {
      id: 'NC',
      img: Images.jurisdiction.northCarolina,
      name: 'North Carolina',
    },
    {
      id: 'SC',
      img: Images.jurisdiction.southCarolina,
      name: 'South Carolina',
    },
  ];

  const getJurisdiction = () => {
    switch (selectedState) {
      case 'IN':
        setJurisdiction('IN01');
        break;
      case 'FL':
        setJurisdiction('FL01');
        break;
      case 'KY':
        setJurisdiction('KY01');
        break;
      case 'OH':
        setJurisdiction('OH01');
        break;

      case 'SC':
        return [
          {
            id: 'SC01',
            img: Images.jurisdiction.scDec,
            name: 'Duke Energy Carolinas',
          },
          {
            id: 'SC02',
            img: Images.jurisdiction.scDep,
            name: 'Duke Energy Progress',
          },
        ];
      case 'NC':
        return [
          {
            id: 'NC01',
            img: Images.jurisdiction.ncDec,
            name: 'Duke Energy Carolinas',
          },
          {
            id: 'NC02',
            img: Images.jurisdiction.ncDep,
            name: 'Duke Energy Progress',
          },
        ];
      default:
        break;
    }

    return null;
  };

  return (
    <>
      {jurisdiction && (
        <JurisdictionDropdown
          jurisdiction={jurisdiction}
          setJurisdictionId={setJurisdictionId}
          setSelectedState={setSelectedState}
        />
      )}
      {!jurisdiction && (
        <div className="bg-gray-lighter w-full flex-shrink overflow-auto">
          <div className="px-24 pb-24 md:pb-48 pt-24 md:pt-48 text-center container-4xl">
            {selectedState && (
              <button className="text-left text-teal-darker font-bold" onClick={backToState}>
                <div className="flex flex-row items-center">
                  <img src={Images.arrow} alt="back-button" className="h-12 w-auto mr-4" /> Back to
                  State
                </div>
              </button>
            )}
            <div className="container-xl flex flex-col gap-24">
              <div>
                {!selectedState && (
                  <Header
                    title="Help us find offers & rebates for your location"
                    description="We need to know your service location in order to display the rebates and special offers available in your area.*"
                  />
                )}
                {selectedState && (
                  <Header
                    title={`Where are you located in ${
                      selectedState === 'NC' ? 'North Carolina' : 'South Carolina'
                    }?`}
                    description="We need a bit more information so we can customize our site for you. We’ll remember your selection for future visits."
                  />
                )}
                <ul
                  aria-labelledby="jurisdiction-intercept-headline"
                  className="flex flex-wrap justify-around gap-y-12 sm:gap-y-32"
                >
                  {!selectedState &&
                    states.map(state => (
                      <li className="w-1/2 sm:w-3/12" key={state.id}>
                        <button
                          className="w-full group-hover:bg-teal-darker transition-colors duration-150"
                          onClick={() => setSelectedState(state.id)}
                        >
                          <div className="icon-48 md:icon-76 bg-teal-darker rounded-full mx-auto">
                            <img src={state.img} alt="" />
                          </div>
                          <div className="bg-gray-lighter w-full mt-16 group-hover:border-current flex flex-row justify-center items-center">
                            <span className="text-teal-darker">{state.name}</span>
                            <img
                              style={{ transform: 'rotate(180deg)' }}
                              src={Images.arrow}
                              alt=""
                              className="max-h-10 w-auto ml-4 rotate-45"
                            />
                          </div>
                        </button>
                      </li>
                    ))}

                  {selectedState &&
                    getJurisdiction()?.map((jurisdiction: JurisdictionType) => (
                      <li
                        className="w-1/3 md:w-auto m-12 mt-0 md:m-0 dec-dep"
                        key={jurisdiction.id}
                      >
                        <button
                          className="flex flex-col gap-12 items-center text-center justify-center w-full text-teal-darker group"
                          onClick={() => selectJurisdiction(jurisdiction.id)}
                        >
                          <div className="w-full">
                            <img src={jurisdiction.img} alt="" />
                          </div>
                          <div className="text-xl flex items-center">
                            {jurisdiction.name}
                            <img
                              style={{ transform: 'rotate(180deg)' }}
                              src={Images.arrow}
                              alt=""
                              className="h-12 md:h-14 lg:h-16 w-auto ml-6 rotate-45 mt-2"
                            />
                          </div>
                        </button>
                      </li>
                    ))}
                </ul>
                {!selectedState && (
                  <p className="italic mx-auto pt-16">
                    *Rebates and special offers are not available in every jurisdiction.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Jurisdiction;
